import React, { useState } from "react";
import { AlertCard, Button, DateDisplay, DateDisplayVariant, Pill } from "@truevault/aquilo";
import { CircularProgress, Typography } from "@mui/material";
import { usePrimaryOrganizationId } from "../../../hooks";
import { usePrivacyCenter } from "../../../hooks/usePrivacyCenter";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";

interface ConsentHistoryEntry {
  timestamp: string;
  consentAnalytics: boolean | null;
  consentAdvertising: boolean | null;
  consentPersonalization: boolean | null;
  consentTargetedAdvertising: boolean | null;
  optedOut: boolean | null;
  implicit: boolean | null;
}

interface ConsumerConsentResult {
  consumerIdentifier: string;
  consumerPartition: string;
  consumerProvider: string;
  privacyCenterId: string;
  clientIds: string[];
  consentAnalytics: boolean | null;
  consentAdvertising: boolean | null;
  consentPersonalization: boolean | null;
  consentTargetedAdvertising: boolean | null;
  optedOut: boolean | null;
  implicit: boolean | null;
  createdAt: string;
  updatedAt: string;
  consentHistory: ConsentHistoryEntry[];
}

const ConsentBadge = ({ value }: { value: boolean | null }) => {
  if (value === null) return <span className="text-gray-400">Default</span>;
  return value ? (
    <Pill variant="completed" noMargin>
      Granted
    </Pill>
  ) : (
    <Pill variant="error" noMargin>
      Not Granted
    </Pill>
  );
};

const OptedOutBadge = ({ value }: { value: boolean | null }) => {
  if (value === null) return <span className="text-gray-400">Not Opted Out</span>;
  return value ? (
    <Pill variant="error" noMargin>
      Opted Out
    </Pill>
  ) : (
    <Pill variant="completed" noMargin>
      Not Opted Out
    </Pill>
  );
};

const getIdentifierLabel = (provider: string): string => {
  if (provider === "email") return "Email";
  if (provider === "shopify") return "Shopify Customer ID";
  return "Identifier";
};

export const ConsumerConsentLookup = () => {
  const orgId = usePrimaryOrganizationId();
  const [privacyCenter] = usePrivacyCenter(orgId);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ConsumerConsentResult | null>(null);

  const handleLookup = async () => {
    if (!query.trim()) {
      setError("Please enter an email, consent cookie clientId, or Shopify Customer ID");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const url = ApiRouteHelpers.organization.privacyCenter.consumerConsentLookup(
        orgId,
        privacyCenter?.id,
      );
      const response = await fetch(`${url}?query=${encodeURIComponent(query.trim())}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setError("No consent record found for the provided identifier");
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.message || `Error: ${response.statusText}`);
        }
        return;
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError("An error occurred while looking up consent data");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLookup();
    }
  };

  // Build history data including current values as the first entry
  const historyData = result
    ? [
        {
          timestamp: result.updatedAt,
          consentAnalytics: result.consentAnalytics,
          consentAdvertising: result.consentAdvertising,
          consentPersonalization: result.consentPersonalization,
          consentTargetedAdvertising: result.consentTargetedAdvertising,
          optedOut: result.optedOut,
          implicit: result.implicit,
          isCurrent: true,
        },
        ...(result.consentHistory || []).map((entry) => ({ ...entry, isCurrent: false })).reverse(),
      ]
    : [];

  return (
    <div className="tw-boundary">
      <div className="max-w-4xl">
        <div className="mb-6">
          <p className="mt-4">
            Look up a consumer's consent history by Shopify Customer ID, Consent Cookie ID ("
            <code>clientId</code>"), or email.
          </p>
          <p>
            <small className="text-gray-500">
              <p>
                Note: For best results, look up the consumer in question in your Shopify dashboard
                and find their Shopify Customer ID. Use that ID to look up their consent history
                here. Email lookup is currently only supported if you are using the custom email
                identity provider for cross-platform consent.
              </p>
              <p className="mt-2">
                This lookup will only return consent records associated with a logged-in Shopify
                customer, and requires using our Shopify app integration. If you need to manage
                consent records for non-Shopify customers, or for more information on the Shopify
                integration, review our{" "}
                <a
                  href="https://developers.truevault.com/docs/cross-platform-consent/overview"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Cross-Platform Consent Management
                </a>{" "}
                implementation guide.
              </p>
            </small>
          </p>
        </div>

        <div className="flex gap-4 items-end mb-6">
          <div className="flex-1">
            <label
              htmlFor="consent-lookup-query"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Shopify Customer ID, Consent Cookie ID, or Email
            </label>
            <input
              id="consent-lookup-query"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="E.g., johndoe@example.com"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <Button color="primary" onClick={handleLookup} disabled={loading || !query.trim()}>
              {loading ? <CircularProgress size={20} color="inherit" /> : "Lookup"}
            </Button>
          </div>
        </div>

        {error && (
          <div className="flex flex-col items-center justify-center py-12 mt-12">
            <img
              src="/assets/images/stay_compliant/heroes/record_lookup.svg"
              alt={error}
              className="w-48 h-48 mb-4"
            />
            <Typography className="text-primary-700 font-medium mt-4">
              No consent records found
            </Typography>
          </div>
        )}

        {result && (
          <div className="space-y-6">
            <AlertCard variant="info" title="Results found" containerClasses="!mt-0">
              <ul className="text-sm mt-2 space-y-1 list-disc list-inside">
                <li>
                  <span className="opacity-70">{getIdentifierLabel(result.consumerProvider)}:</span>{" "}
                  <span className="font-medium">{result.consumerIdentifier}</span>
                </li>
                <li>
                  <span className="opacity-70">Shop:</span>{" "}
                  <span className="font-medium">{result.consumerPartition}</span>
                </li>
                <li>
                  <span className="opacity-70">First seen:</span>{" "}
                  <span className="font-medium">
                    <DateDisplay
                      date={result.createdAt}
                      variant={DateDisplayVariant.MEDIUM}
                      hoverForRelative
                      inline
                    />
                  </span>
                </li>
                {result.clientIds && result.clientIds.length > 0 && (
                  <li>
                    <span className="opacity-70">
                      {result.clientIds.length > 1 ? "Consent Cookie IDs:" : "Consent Cookie ID:"}
                    </span>
                    <ul className="mt-1 ml-4 text-xs font-mono opacity-70 space-y-0.5">
                      {result.clientIds.map((clientId) => (
                        <li key={clientId}>{clientId}</li>
                      ))}
                    </ul>
                  </li>
                )}
              </ul>
            </AlertCard>

            <div>
              <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-4">
                Consent History
              </h3>
              {historyData.length > 0 ? (
                <div className="flex flex-col" style={{ gap: "2rem" }}>
                  {historyData.map((entry) => (
                    <div key={`${entry.timestamp}-${entry.isCurrent}`}>
                      <div
                        className="flex justify-between items-center gap-2 mb-2 pb-1 border-bottom border-bottom--grey"
                        style={{ maxWidth: "320px" }}
                      >
                        <h4 className="font-medium text-gray-700">
                          <DateDisplay
                            date={entry.timestamp}
                            variant={DateDisplayVariant.FULL}
                            inline
                          />
                        </h4>
                        {entry.isCurrent && (
                          <Pill variant="info" noMargin>
                            Current
                          </Pill>
                        )}
                      </div>
                      <div className="flex flex-col text-sm" style={{ maxWidth: "320px" }}>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">
                            Consumer {entry.isCurrent ? "is" : "was"}
                          </span>
                          <OptedOutBadge value={entry.optedOut} />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Analytics</span>
                          <ConsentBadge value={entry.consentAnalytics} />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Targeted Advertising</span>
                          <ConsentBadge value={entry.consentTargetedAdvertising} />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Personalization</span>
                          <ConsentBadge value={entry.consentPersonalization} />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Advertising</span>
                          <ConsentBadge value={entry.consentAdvertising} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Typography className="text-gray-500">No consent history available.</Typography>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
